import React, { useEffect, useRef, useState } from 'react';
import { X, Download, Copy, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';

export default function CotizacionPuertaPuertaImagenModal({ solicitud, onClose, onEnviada }) {
  const canvasRef = useRef(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!solicitud) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = 2;
    const W = 480;
    const H = 420;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = W + 'px';
    canvas.style.height = H + 'px';
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    const YELLOW = '#FEBE00';
    const DARK = '#111111';
    const WHITE = '#FFFFFF';
    const GRAY_TEXT = '#666666';
    const BORDER = '#e5e7eb';

    ctx.fillStyle = WHITE;
    ctx.fillRect(0, 0, W, H);

    // Header
    ctx.fillStyle = YELLOW;
    ctx.fillRect(0, 0, W, 80);
    ctx.fillStyle = DARK;
    ctx.font = 'bold 28px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('ECUAFLY', W / 2, 38);
    ctx.font = '11px system-ui, -apple-system, sans-serif';
    ctx.fillStyle = '#333';
    ctx.fillText('Puerta a Puerta', W / 2, 58);

    ctx.fillStyle = DARK;
    ctx.font = 'bold 17px system-ui, -apple-system, sans-serif';
    ctx.fillText('COTIZACIÓN DE ENVÍO', W / 2, 110);

    ctx.strokeStyle = YELLOW;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(40, 120);
    ctx.lineTo(W - 40, 120);
    ctx.stroke();

    let y = 145;
    const leftX = 40;
    const rightX = W - 40;

    const drawRow = (label, value) => {
      ctx.fillStyle = DARK;
      ctx.font = '13px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(label, leftX, y);
      ctx.font = 'bold 13px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(value, rightX, y);
      y += 24;
    };

    drawRow('Cliente:', solicitud.nombre_completo);
    drawRow('Origen:', `${solicitud.ciudad_origen}, ${solicitud.estado_usa}`);
    drawRow('Destino:', `${solicitud.ciudad_destino_ec}, ${solicitud.provincia_ec}`);
    y += 14;

    // Total — destacado
    ctx.fillStyle = DARK;
    ctx.fillRect(leftX, y - 18, rightX - leftX, 44);
    ctx.fillStyle = WHITE;
    ctx.font = 'bold 14px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('TOTAL A PAGAR', leftX + 12, y + 6);
    ctx.fillStyle = YELLOW;
    ctx.font = 'bold 22px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText('$' + Number(solicitud.cotizacion_cliente).toFixed(2), rightX - 12, y + 8);
    y += 44;

    ctx.fillStyle = GRAY_TEXT;
    ctx.font = '10px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Incluye retiro, envío hasta Miami y comisión de la plataforma de pago.', W / 2, y);
    y += 26;

    ctx.strokeStyle = BORDER;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(leftX, y);
    ctx.lineTo(rightX, y);
    ctx.stroke();
    y += 22;

    ctx.fillStyle = DARK;
    ctx.font = 'bold 13px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Forma de pago: Payphone (tarjeta)', leftX, y);
    y += 18;
    ctx.font = '11px system-ui, -apple-system, sans-serif';
    ctx.fillStyle = '#374151';
    ctx.fillText('Te enviamos el link de pago por separado.', leftX, y);

    // Footer
    const footerY = H - 50;
    ctx.fillStyle = DARK;
    ctx.fillRect(0, footerY, W, H - footerY);
    ctx.fillStyle = YELLOW;
    ctx.font = 'bold 12px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('ECUAFLY', W / 2, footerY + 20);
    ctx.fillStyle = '#9ca3af';
    ctx.font = '9px system-ui, -apple-system, sans-serif';
    ctx.fillText('Miami · Shenzhen · Quito  |  info@ecuafly.com', W / 2, footerY + 36);

    setImgUrl(canvas.toDataURL('image/png'));
  }, [solicitud]);

  const descargar = () => {
    if (!imgUrl) return;
    const link = document.createElement('a');
    link.download = `cotizacion_puerta_puerta_${solicitud.id}.png`;
    link.href = imgUrl;
    link.click();
    toast.success('Imagen descargada.');
  };

  const copiar = async () => {
    if (!canvasRef.current) return;
    try {
      const blob = await new Promise((resolve) => canvasRef.current.toBlob(resolve, 'image/png'));
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      toast.success('Imagen copiada al portapapeles.');
    } catch {
      toast.error('No se pudo copiar. Descarga la imagen en su lugar.');
    }
  };

  const enviarCorreo = async () => {
    setEnviando(true);
    try {
      const res = await api.post(`/puerta-a-puerta/${solicitud.id}/enviar-cotizacion-correo/`);
      toast.success(`Correo enviado a ${solicitud.email}.`);
      if (onEnviada) onEnviada(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo enviar el correo.');
    } finally {
      setEnviando(false);
    }
  };

  if (!solicitud) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '540px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Cotización para WhatsApp / Correo</h3>
          <button className="btn-ghost" onClick={onClose}><X size={18} /></button>
        </div>

        <div style={{ textAlign: 'center', marginTop: 'var(--spacing-4)' }}>
          <canvas ref={canvasRef} style={{ maxWidth: '100%', borderRadius: 'var(--radius-md)', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }} />
          <div style={{ display: 'flex', gap: 'var(--spacing-3)', justifyContent: 'center', marginTop: 'var(--spacing-4)' }}>
            <button className="btn-primary" onClick={descargar}>
              <Download size={16} /> Descargar imagen
            </button>
            <button className="btn-secondary" onClick={copiar}>
              <Copy size={16} /> Copiar al portapapeles
            </button>
          </div>
          <p className="text-xs text-secondary" style={{ marginTop: 'var(--spacing-3)' }}>
            Pega la imagen en WhatsApp, o envía la cotización por correo con el botón de abajo.
          </p>
          <button className="btn-primary" onClick={enviarCorreo} disabled={enviando} style={{ marginTop: 'var(--spacing-2)' }}>
            <Mail size={16} /> {enviando ? 'Enviando...' : `Enviar por correo a ${solicitud.email}`}
          </button>
        </div>
      </div>
    </div>
  );
}
